import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const INDUSTRIES = [
  {
    icon: 'ri-ship-line',
    title: 'Maritime & Shipping',
    desc: 'Custom ship chandling quotation systems, cargo management, and AI-powered supply chain solutions for ports and shipping companies.',
    solutions: ['Ship Chandling Quotation System', 'Cargo Management', 'Fleet Tracking', 'Port Operations Dashboard'],
    img: 'https://readdy.ai/api/search-image?query=maritime%20shipping%20port%20operations%20modern%20technology%20software%20management%20container%20ships%20ocean%20harbor%20professional%20business%20minimal%20clean%20light%20background%20teal%20accents&width=400&height=260&seq=ind-maritime&orientation=landscape',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail & E-Commerce',
    desc: 'POS systems, inventory management, and AI-driven analytics for single stores to multi-chain retail enterprises.',
    solutions: ['POS Solution', 'Inventory Management', 'Customer Analytics', 'E-Commerce Integration'],
    img: 'https://readdy.ai/api/search-image?query=modern%20retail%20store%20technology%20POS%20system%20digital%20inventory%20management%20e-commerce%20clean%20professional%20bright%20interior%20contemporary%20minimalist&width=400&height=260&seq=ind-retail&orientation=landscape',
  },
  {
    icon: 'ri-book-2-line',
    title: 'Education & Libraries',
    desc: 'RFID library management systems, student information systems, and digital learning platforms for schools and universities.',
    solutions: ['RFID Library Management', 'Student Information System', 'E-Learning Platform', 'Campus Management'],
    img: 'https://readdy.ai/api/search-image?query=modern%20university%20library%20education%20technology%20digital%20learning%20RFID%20system%20bright%20clean%20contemporary%20academic%20institution%20light%20colors%20professional&width=400&height=260&seq=ind-edu&orientation=landscape',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Healthcare',
    desc: 'AI-powered patient analytics, hospital management systems, and secure health information platforms compliant with global standards.',
    solutions: ['Patient Analytics AI', 'Hospital Management System', 'Medical Records Platform', 'Appointment Scheduling'],
    img: 'https://readdy.ai/api/search-image?query=modern%20hospital%20healthcare%20technology%20digital%20management%20system%20medical%20analytics%20clean%20bright%20professional%20technology%20interface%20light%20background%20minimal&width=400&height=260&seq=ind-health&orientation=landscape',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Manufacturing',
    desc: 'Smart factory solutions, RFID asset tracking, production monitoring, and quality control systems for manufacturers.',
    solutions: ['RFID Asset Tracking', 'Production Monitoring', 'Quality Management', 'Warehouse Management'],
    img: 'https://readdy.ai/api/search-image?query=smart%20factory%20manufacturing%20technology%20digital%20monitoring%20system%20production%20floor%20management%20professional%20clean%20modern%20industrial%20technology%20light%20background&width=400&height=260&seq=ind-manuf&orientation=landscape',
  },
  {
    icon: 'ri-bank-line',
    title: 'Finance & Banking',
    desc: 'Secure, ISO/IEC 27001:2022 compliant financial software, compliance management systems, and AI risk assessment tools.',
    solutions: ['Compliance Management', 'AI Risk Assessment', 'Transaction Analytics', 'Secure Banking Portals'],
    img: 'https://readdy.ai/api/search-image?query=financial%20banking%20technology%20software%20dashboard%20analytics%20secure%20compliance%20modern%20clean%20professional%20light%20background%20minimal%20teal%20accent&width=400&height=260&seq=ind-finance&orientation=landscape',
  },
  {
    icon: 'ri-building-line',
    title: 'Real Estate',
    desc: 'Property management systems, AI-powered valuations, tenant portals, and smart building management platforms.',
    solutions: ['Property Management System', 'AI Valuation Tools', 'Tenant Portal', 'Smart Building IoT'],
    img: 'https://readdy.ai/api/search-image?query=real%20estate%20property%20management%20software%20technology%20platform%20modern%20buildings%20digital%20solutions%20clean%20professional%20light%20background%20minimal%20contemporary&width=400&height=260&seq=ind-realestate&orientation=landscape',
  },
  {
    icon: 'ri-government-line',
    title: 'Government & Public Sector',
    desc: 'Citizen service platforms, document management systems, and public sector ERP solutions with full compliance.',
    solutions: ['Citizen Service Portal', 'Document Management', 'Public ERP System', 'E-Governance Platform'],
    img: 'https://readdy.ai/api/search-image?query=government%20public%20sector%20digital%20technology%20platform%20citizen%20services%20e-governance%20modern%20clean%20professional%20building%20minimal%20light%20background&width=400&height=260&seq=ind-govt&orientation=landscape',
  },
];

export default function IndustriesPage() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              Industries
            </span> */}
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Transforming <span className="text-teal-500">Every Industry</span>
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto">
              Our 25 years of cross-industry expertise means we understand the unique challenges and opportunities in your sector.
            </p>
          </div>
        </section>

        {/* Industries Grid */}
        <section ref={ref} className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid md:grid-cols-2 gap-8">
              {INDUSTRIES.map((ind, i) => (
                <div
                  key={ind.title}
                  className={`group bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden hover:border-teal-200 transition-all duration-500 ${
                    inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-full h-52 overflow-hidden">
                    <img
                      src={ind.img}
                      alt={ind.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl">
                        <i className={`${ind.icon} text-2xl`} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{ind.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">{ind.desc}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {ind.solutions.map((s) => (
                        <div key={s} className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-check-line text-teal-500" /> {s}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Don&apos;t See Your Industry?</h2>
            <p className="text-teal-100 text-base mb-8">We build custom solutions for any industry. Let&apos;s discuss your specific needs.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-message-line" /> Contact Our Team
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
